import { Route, Switch, useParams } from 'react-router-dom';
import {
  SubNavigationNavBar,
  SubNavigationNavLink,
} from 'components/Navigation/SubNavigation';
import { useCallback, useContext, useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Toolbar } from '__pages/Common/styles/ControlBar';
import BackButton from '__components/Button/BackButton';
import { SettingsContentCard } from '__components/Card/Card';
import { Spacer } from '@kizen/kds/Spacer';
import { PluginContext } from 'ts-components/Plugins/context';
import { PageSizing } from '__components/Layout/PageContentWidth';
import { useQuery } from '@tanstack/react-query';
import PluginService from 'services/PluginService';
import { PLUGINS } from '__queries/query-keys';
import { MarketplacePlugin } from '../types';
import Loader from '__components/Kizen/Loader';
import { DetailsPanel } from './panels/Details';
import { ConfigurePanel } from './panels/Configure';
import { VersionsPanel } from './panels/Versions';
import { PageToolbarTitle } from '__components/Layout/PageToolbar';
import { usePluginEnable } from './usePluginEnable';
import { SetupAssistant } from '../components/SetupAssistant';
import { EditIntegrationSecretModal } from '__pages/Settings/pages/ManageConnections/IntegrationSecrets/IntegrationSecretModal';
import { NoPermissionModal } from '../components/Tile/NoPermissionModal';
import { StyledToolbarLeft, StyledToolbarRight } from '../styles';
import { useSetTitleOnLoad } from '__hooks/useSetTitleOnLoad';
import { AuthorizationPanel } from './panels/Authorization';
import { useMarketplacePermissions } from '../permissions';
import { UserSettingsPanel } from './panels/UserSettings';
import { InstalledPlugin } from 'ts-components/Plugins/types';
import { useBusinessPluginApps } from 'ts-components/Plugins/useBusinessPluginApps';

export const MarketplaceDetailsPage = () => {
  const params = useParams<{ pluginAppApiName: string }>();

  const { pluginAppApiName } = params;

  const { t } = useTranslation();

  const {
    plugins: installedPlugins,
    isLoading: isLoadingPluginContext,
    refreshPluginArtifacts,
  } = useContext(PluginContext);

  const { data: businessPluginApps, isLoading: isLoadingBusinessPluginApps } =
    useBusinessPluginApps(true);

  const { data: marketplacePlugin, isLoading: isLoadingMarketplacePlugin } =
    useQuery<MarketplacePlugin>({
      queryFn: () => PluginService.getPluginApp(pluginAppApiName),
      queryKey: PLUGINS.GET_PLUGIN_APP(pluginAppApiName),
    });

  const userSetupAssistant = useMemo(() => {
    return marketplacePlugin?.base_config?.user_setup_assistant;
  }, [marketplacePlugin]);

  const hasUserSetup = Boolean(userSetupAssistant);

  const businessPluginAppsByApiName = useMemo(() => {
    return (
      businessPluginApps?.reduce((acc: any, curr: any) => {
        return {
          ...acc,
          [curr.plugin_app.api_name]: curr,
        };
      }, {}) ?? {}
    );
  }, [businessPluginApps]);

  const installation: InstalledPlugin | undefined = useMemo(() => {
    return installedPlugins[pluginAppApiName];
  }, [installedPlugins, pluginAppApiName]);

  const businessPluginApp = useMemo(() => {
    return businessPluginAppsByApiName[pluginAppApiName];
  }, [businessPluginAppsByApiName, pluginAppApiName]);

  const oAuthServices = useMemo(() => {
    return (
      businessPluginApp?.plugin_app?.services?.filter(
        (s: any) => s.auth_type === 'oauth'
      ) ?? []
    );
  }, [businessPluginApp]);

  const hasKZNFileEverImported = Boolean(
    businessPluginApp?.config?.__kizen_schema_import_etag
  );

  const hasOauthServices = Boolean(oAuthServices.length > 0);

  useSetTitleOnLoad(
    t('App Marketplace - {{name}}', {
      name: marketplacePlugin?.name,
    })
  );

  const businessPluginAppId = businessPluginApp?.id ?? '';

  const { data: pluginAppEvents, isLoading: isLoadingPluginAppEvents } =
    useQuery({
      // Fetching events has fallback logic for empty IDs, so it's safe to call even if the ID is an empty string
      queryFn: () => PluginService.getEvents(businessPluginAppId),
      queryKey: PLUGINS.GET_EVENTS(businessPluginAppId),
      // Always refetch on focus because we need this state to be up to date when returning to the tab after authorizing
      refetchOnWindowFocus: 'always',
      // We poll for new events if there are OAuth services or if a KZN file has ever been imported, since both of these can have external changes
      // that need to show in relatively real-time
      refetchInterval:
        hasOauthServices || hasKZNFileEverImported ? 15000 : false,
    });

  const oAuthEvents = useMemo(() => {
    return (
      pluginAppEvents?.events.filter((e: any) =>
        e.resource_path.includes('oauth')
      ) ?? []
    );
  }, [pluginAppEvents]);

  const importEvents = useMemo(() => {
    return (
      pluginAppEvents?.events.filter((e: any) => e.tag.includes('import')) ?? []
    );
  }, [pluginAppEvents]);

  const importErrorEvents = useMemo(() => {
    return importEvents.filter((e: any) => e.event_type === 'error');
  }, [importEvents]);

  const oAuthErrorEvents = useMemo(() => {
    return oAuthEvents.filter((e: any) => e.event_type === 'error');
  }, [oAuthEvents]);

  const { showConfigPanel, canEditJSON, canViewPlugins } =
    useMarketplacePermissions(marketplacePlugin);

  const routes = useMemo(() => {
    return [
      {
        label: (t: TFunction) => t('App Details'),
        path: `/marketplace/${pluginAppApiName}/details`,
        icon: 'info-circle',
        exact: true,
        Component: DetailsPanel,
        badge: importErrorEvents?.length > 0,
      },
      showConfigPanel(businessPluginApp)
        ? {
            label: (t: TFunction) => t('App Settings'),
            path: `/marketplace/${pluginAppApiName}/configure`,
            icon: 'cog',
            exact: true,
            Component: ConfigurePanel,
          }
        : undefined,
      hasUserSetup && Boolean(businessPluginApp)
        ? {
            label: (t: TFunction) => t('User Settings'),
            path: `/marketplace/${pluginAppApiName}/user-settings`,
            icon: 'user',
            exact: true,
            Component: UserSettingsPanel,
          }
        : undefined,
      canViewPlugins
        ? {
            label: (t: TFunction) => t('Version'),
            path: `/marketplace/${pluginAppApiName}/version`,
            icon: 'cloud-arrow-up',
            exact: true,
            Component: VersionsPanel,
          }
        : undefined,
      hasOauthServices
        ? {
            label: (t: TFunction) => t('Authorization'),
            path: `/marketplace/${pluginAppApiName}/auth`,
            icon: 'shield-check',
            badge: oAuthErrorEvents?.length > 0,
            exact: true,
            Component: AuthorizationPanel,
          }
        : null,
    ].filter(Boolean) as Array<{
      label: (t: TFunction) => string;
      path: string;
      icon: string;
      exact: boolean;
      badge?: boolean;
      Component: React.FC<any>;
    }>;
  }, [
    pluginAppApiName,
    showConfigPanel,
    hasOauthServices,
    oAuthErrorEvents,
    businessPluginApp,
    importErrorEvents,
    hasUserSetup,
    canViewPlugins,
  ]);

  const refetch = useCallback(
    () =>
      refreshPluginArtifacts(pluginAppApiName, {
        pluginAppId: marketplacePlugin?.id,
      }),
    [refreshPluginArtifacts, pluginAppApiName, marketplacePlugin?.id]
  );

  const {
    onChangeEnabled,
    secretsToEdit,
    setupAssistantConfig,
    pendingChange,
    noPermissionModalProps,
    setupAssistantPending,
    handleCloseSecretsInlineModal,
    handleCloseSetupAssistant,
    canEditIntegrationSecrets,
    onChangeSetupAssistantConfig,
    onImportKZNFileStep,
  } = usePluginEnable({
    businessPluginAppsByApiName,
    refetch,
  });

  const isLoading =
    isLoadingPluginContext ||
    isLoadingBusinessPluginApps ||
    isLoadingMarketplacePlugin ||
    isLoadingPluginAppEvents;

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <div>
      <Spacer size={20} />
      <SubNavigationNavBar>
        {routes.map((route, index) => {
          return (
            <SubNavigationNavLink
              key={`${route.path}-link`}
              route={{
                label: route.label(t),
                path: route.path,
                icon: route.icon,
                badge: route.badge,
              }}
              numberOfLinks={routes.length}
              isLast={index === routes.length - 1}
            />
          );
        })}
      </SubNavigationNavBar>
      <Spacer size={20} />
      <PageSizing>
        <Toolbar>
          <StyledToolbarLeft>
            <BackButton to="/marketplace">{t('back')}</BackButton>
          </StyledToolbarLeft>
          <PageToolbarTitle>{marketplacePlugin?.name}</PageToolbarTitle>
          <StyledToolbarRight
            style={{
              minWidth: '100px',
              maxWidth: '100px',
            }}
          ></StyledToolbarRight>
        </Toolbar>
      </PageSizing>
      <Spacer size={20} />
      <Switch>
        {routes.map((route) => {
          return (
            <Route key={route.path} exact={route.exact} path={route.path}>
              <PageSizing>
                <SettingsContentCard>
                  <route.Component
                    plugin={marketplacePlugin}
                    installedPlugin={installation}
                    businessPluginAppsByApiName={businessPluginAppsByApiName}
                    refetch={refetch}
                    businessPluginApp={businessPluginApp}
                    canEditJSON={canEditJSON}
                    pendingChange={pendingChange}
                    onChangeEnabled={onChangeEnabled}
                    oAuthServices={oAuthServices}
                    oAuthEvents={oAuthEvents}
                    onImportKZNFileStep={onImportKZNFileStep}
                    importEvents={importEvents}
                  />
                </SettingsContentCard>
                <Spacer size={30} />
              </PageSizing>
            </Route>
          );
        })}
      </Switch>
      {setupAssistantPending && setupAssistantConfig ? (
        <SetupAssistant
          onClose={handleCloseSetupAssistant}
          config={setupAssistantConfig}
          onChangeConfig={onChangeSetupAssistantConfig}
          businessPluginAppsByApiName={businessPluginAppsByApiName}
          onImportKZNFileStep={onImportKZNFileStep}
        />
      ) : null}
      {canEditIntegrationSecrets &&
      secretsToEdit.length > 0 &&
      !setupAssistantPending ? (
        <EditIntegrationSecretModal
          key={secretsToEdit[0].id}
          selectedIntegrationSecret={secretsToEdit[0]}
          // setSelectedIntegrationSecret is called after the modal is closed, either by create
          // or skip. In either case, we don't need to change anything in the state, just move on to the
          // next secret in the queue if available
          setSelectedIntegrationSecret={handleCloseSecretsInlineModal}
          initialMode
        />
      ) : null}
      <NoPermissionModal {...noPermissionModalProps} />
    </div>
  );
};
